'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, Award, Clock, MapPin, User, Users } from 'lucide-react'
import { toast } from '@warfighter/ui'
import { createBrowserSupabaseClient } from '@/lib/supabaseClient'
import { calculatePcsDelta } from '@/app/actions'

type PCSFormData = {
  rank: string
  yearsOfService: number
  currentZip: string
  currentState: string
  targetZip: string
  targetState: string
  hasDependents: boolean
  isStateTaxExempt: boolean
}

type PCSResult = {
  current: {
    monthlyNet: number
    annualNet: number
    bah: number
  }
  target: {
    monthlyNet: number
    annualNet: number
    bah: number
  }
  delta: {
    monthly: number
    annual: number
    percent: number
  }
}

type PCSCalculatorProps = {
  initialValues?: Partial<PCSFormData>
}

export default function PCSCalculator({ initialValues }: PCSCalculatorProps) {
  const supabase = useMemo(() => createBrowserSupabaseClient(), [])
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<PCSResult | null>(null)
  const initialValuesApplied = useRef(false)
  const [formData, setFormData] = useState<PCSFormData>({
    rank: 'E-5',
    yearsOfService: 6,
    currentZip: '92101',
    currentState: 'CA',
    targetZip: '35801',
    targetState: 'AL',
    hasDependents: true,
    isStateTaxExempt: false
  })

  useEffect(() => {
    if (!initialValues || initialValuesApplied.current) return
    setFormData((prev) => ({ ...prev, ...initialValues }))
    initialValuesApplied.current = true
  }, [initialValues])

  const resolveStateFromZip = async (
    zip: string,
    field: 'currentState' | 'targetState'
  ) => {
    const trimmed = zip.trim()
    if (trimmed.length !== 5) return
    const { data } = await supabase
      .from('zip_codes')
      .select('state_abbr')
      .eq('zip', trimmed)
      .maybeSingle()
    const state = data?.state_abbr?.trim().toUpperCase()
    if (!state) return
    setFormData((prev) => {
      if (field === 'currentState' && prev.currentZip !== trimmed) return prev
      if (field === 'targetState' && prev.targetZip !== trimmed) return prev
      return prev[field] === state ? prev : { ...prev, [field]: state }
    })
  }

  useEffect(() => {
    if (!formData.currentZip) return
    const handle = window.setTimeout(() => {
      resolveStateFromZip(formData.currentZip, 'currentState')
    }, 400)
    return () => window.clearTimeout(handle)
  }, [formData.currentZip, supabase])

  useEffect(() => {
    if (!formData.targetZip) return
    const handle = window.setTimeout(() => {
      resolveStateFromZip(formData.targetZip, 'targetState')
    }, 400)
    return () => window.clearTimeout(handle)
  }, [formData.targetZip, supabase])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    try {
      const data = await calculatePcsDelta(formData)
      setResult(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast.error(message)
    }
    setLoading(false)
  }

  const deltaMonthly = result?.delta.monthly ?? 0
  const deltaAnnual = result?.delta.annual ?? 0
  const deltaPercent = result?.delta.percent ?? 0
  const isPositive = deltaMonthly >= 0
  const changeLabel = isPositive ? 'raise' : 'pay cut'
  const changeColor = isPositive ? 'text-emerald-600' : 'text-rose-500'

  return (
    <div className="mx-auto max-w-4xl rounded-3xl bg-white p-8 shadow-2xl shadow-blue-100 md:p-12">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">PCS Pay Change</h1>
        <p className="text-sm text-slate-500">
          Compare your take-home pay between duty stations.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-8">
        <section className="space-y-4">
          <p className="text-sm font-semibold text-slate-700">The Operator</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Rank
              </label>
              <div className="relative">
                <Award className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <select
                  className="h-12 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-sm font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.rank}
                  onChange={(e) => setFormData({ ...formData, rank: e.target.value })}
                >
                  {[
                    'E-1',
                    'E-2',
                    'E-3',
                    'E-4',
                    'E-5',
                    'E-6',
                    'E-7',
                    'E-8',
                    'E-9',
                    'W-1',
                    'W-2',
                    'W-3',
                    'O-1',
                    'O-2',
                    'O-3',
                    'O-4',
                    'O-5',
                    'O-6'
                  ].map((rank) => (
                    <option key={rank} value={rank}>
                      {rank}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Years of Service
              </label>
              <div className="relative">
                <Clock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="number"
                  className="h-12 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-sm font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.yearsOfService}
                  onChange={(e) =>
                    setFormData({ ...formData, yearsOfService: Number(e.target.value) })
                  }
                />
              </div>
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Dependents
              </label>
              <div className="grid grid-cols-2 rounded-full bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, hasDependents: false })}
                  className={`flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                    !formData.hasDependents
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500'
                  }`}
                >
                  <User className="h-4 w-4" />
                  Single
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, hasDependents: true })}
                  className={`flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                    formData.hasDependents
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500'
                  }`}
                >
                  <Users className="h-4 w-4" />
                  With Dependents
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <p className="text-sm font-semibold text-slate-700">The Move</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Current Duty Station
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter ZIP code"
                  className="h-14 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-base font-semibold text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.currentZip}
                  onChange={(e) => setFormData({ ...formData, currentZip: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 py-1">
              <div className="h-px flex-1 bg-slate-200" />
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500/10">
                <ArrowDown className="h-3.5 w-3.5 text-blue-500" />
              </div>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Gaining Duty Station
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-600" />
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter ZIP code"
                  className="h-14 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-base font-semibold text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.targetZip}
                  onChange={(e) => setFormData({ ...formData, targetZip: e.target.value })}
                />
              </div>
            </div>
          </div>
        </section>

        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            checked={formData.isStateTaxExempt}
            onChange={(e) =>
              setFormData({ ...formData, isStateTaxExempt: e.target.checked })
            }
          />
          <span>
            State Income Tax Exempt? (e.g., FL/TX Resident)
            <span className="mt-1 block text-xs text-slate-500">
              Overrides state tax differences.
            </span>
          </span>
        </label>

        <button
          type="submit"
          disabled={loading}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] text-sm font-semibold text-[var(--accent-contrast)] shadow-lg shadow-[var(--accent)]/30 transition hover:bg-[var(--accent-hover)]"
        >
          {loading ? 'Calculating...' : 'Calculate Change'}
        </button>
      </form>

      {result && (
        <div className="mt-10 rounded-3xl border border-slate-200 bg-slate-50 p-6">
          <div className="text-center">
            <p className="text-xs uppercase tracking-wide text-slate-500">
              PCS Pay Change
            </p>
            <p className={`mt-2 text-4xl font-extrabold ${changeColor}`}>
              {deltaPercent >= 0 ? '+' : '-'}
              {Math.abs(deltaPercent).toFixed(1)}%
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {deltaMonthly >= 0 ? '+' : '-'}${Math.abs(deltaMonthly).toLocaleString()} / mo •{' '}
              {deltaAnnual >= 0 ? '+' : '-'}${Math.abs(deltaAnnual).toLocaleString()} / yr
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-700">
              That&apos;s a {changeLabel} in take-home pay.
            </p>
          </div>

          <div className="mt-6 space-y-3 text-sm text-slate-600">
            <p className="flex justify-between">
              <span>Current Monthly Take-Home:</span>
              <span className="font-semibold">
                ${result.current.monthlyNet.toLocaleString()}
              </span>
            </p>
            <p className="flex justify-between">
              <span>Gaining Monthly Take-Home:</span>
              <span className="font-semibold">
                ${result.target.monthlyNet.toLocaleString()}
              </span>
            </p>
            <p className="flex justify-between">
              <span>Current BAH:</span>
              <span className="font-semibold">${result.current.bah.toLocaleString()}</span>
            </p>
            <p className="flex justify-between">
              <span>Gaining BAH:</span>
              <span className="font-semibold">${result.target.bah.toLocaleString()}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
